import React, { useState, useEffect, useRef } from 'react'
import moment from 'moment'
import { format, parseISO, addHours } from 'date-fns'

export const formatDate = date => moment(date).format('MM/DD/YYYY')
export const formatDateTime = datetime => format(parseISO(datetime), 'MMM do yyyy h:mm aa')
export const formatDateTimeNoConversion = datetime => format(addHours(parseISO(datetime), 6), 'MMM do yyyy h:mm aa')

export const searchFilter = array => search => Promise.resolve(array.filter(({ label }) => label.toLowerCase().includes(search.toLowerCase())).slice(0, 50))

export const validationFields = ['crewPercent', 'dateTimeEnd', 'dateTimeStart', 'plowsOrTechs', 'quantityMaterial', 'routeId']

export const hasError = (errors, field) => errors && errors.fieldsWithError.includes(field)

export const shouldValidate = (row) => !!validationFields.filter(f => row[f]).length

const defaultValidation = (row, field) => row[field] !== void 0 && row[field] !== null && row[field] !== ''

export const isValidInput = (row, field) => {
  const negativeIntValidation = () => parseFloat(row[field]) >= 0

  const momentDate = () => moment(row[field])
  const dateValidation = () => {
    const currentYear = moment().year()
    const minYear = currentYear - 1
    const maxYear = currentYear + 1
    const d = momentDate()
    return d.isValid() && d.year() <= maxYear && d.year() >= minYear
  }

  switch (field) {
    case 'routeId':
      return row.propNameAddress !== null && row.propNameAddress !== void 0 && row.propNameAddress !== ''

    case 'dateTimeStart':
      return defaultValidation(row, field) && dateValidation() && momentDate().isSameOrBefore(moment(row.dateTimeEnd))

    case 'dateTimeEnd':
      return defaultValidation(row, field) && dateValidation() && momentDate().isSameOrAfter(moment(row.dateTimeStart))

    case 'crewPercent':
      return defaultValidation(row, field) && negativeIntValidation() && parseFloat(row[field]) <= 100

    case 'quantityMaterial':
      return (row.extraData && !row.extraData.Stocking_Units) || (defaultValidation(row, field) && negativeIntValidation())

    default:
      return defaultValidation(row, field) && negativeIntValidation()
  }
}

export const getErrorMsg = (field) => {
  switch (field) {
    case 'routeId':
      return 'Invalid Route'

    case 'dateTimeStart':
      return 'Invalid Date'

    case 'dateTimeEnd':
      return 'Invalid Date'

    default:
      return 'Required'
  }
}

export const shouldRemoveError = (errors, field, row) => {
  const hasData = shouldValidate(row)
  return hasError(errors, field) && ((hasData && isValidInput(row, field)) || !hasData)
}

export const copyAndRemoveError = (errors, field) => {
  const idxOfField = errors.fieldsWithError.findIndex(f => f === field)
  const fieldsWithErrorCopy = errors.fieldsWithError.slice()
  fieldsWithErrorCopy.splice(idxOfField, 1)
  const errorsCopy = Object.assign({}, errors)
  errorsCopy.fieldsWithError = fieldsWithErrorCopy
  return errorsCopy
}

export const shallowCompare = (newObj, prevObj) => {
  if (newObj && prevObj) {
    const newObjKeys = Object.keys(newObj)
    const prevObjKeys = Object.keys(prevObj)
    const keys = newObjKeys.length ? newObjKeys : prevObjKeys
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i]
      if (key !== 'ref') {
        if (newObj[key] !== prevObj[key]) return true
      }
    }
    return false
  }
  return false
}

export const logServiceError = (name, error) => {
  if (error.response) {
    // The request was made and the server responded with a status code
    // that falls out of the range of 2xx
    console.error('response', error.response)
  }
  if (error.request) {
    // The request was made but no response was received
    // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
    // http.ClientRequest in node.js
    console.error('request', error.request)
  } else {
    // Something happened in setting up the request that triggered an Error
    console.error(name, error.message)
  }
}

export const removeRefs = rows => {
  return rows.map(r => {
    const rowCopy = Object.assign({}, r)
    delete rowCopy.ref
    delete rowCopy.dateRef
    return rowCopy
  })
}

const isRowEmpty = row => {
  const keys = Object.keys(row)
  const keysLength = keys.length
  if (!keysLength) return true
  for (let i = 0; i < keysLength; i++) {
    const key = keys[i]
    if (defaultValidation(row, key)) return false
  }
  return true
}

export const prepareRowsForSaving = rows => {
  return rows.reduce((accum, row, idx) => {
    const rowCopy = Object.assign({}, row)
    delete rowCopy.ref
    delete rowCopy.dateRef
    delete rowCopy.dateEndRef
    if (!isRowEmpty(rowCopy)) {
      rowCopy.rowIdx = idx
      accum.push(rowCopy)
    }
    return accum
  }, [])
}

export const getTitle = () => {
  switch (History.location.pathname) {
    case '/users':
      return 'Manage Users'
    case '/time-sheet':
      return 'Time Sheet Entry'
    case '/map':
      return 'Map'
    case '/help':
      return 'FAQ'
    default:
      return 'Martz Bros'
  }
}

export const parseQuery = (queryString) => {
  const query = {}
  const pairs = (queryString[0] === '?' ? queryString.substr(1) : queryString).split('&')
  for (let i = 0; i < pairs.length; i++) {
    const pair = pairs[i].split('=')
    query[decodeURIComponent(pair[0])] = decodeURIComponent(pair[1] || '')
  }
  return query
}

export const getHoursDiff = (start, end, plowsOrTechs) => end && start && moment.duration(moment(end).diff(start)).asHours() * (plowsOrTechs || 0) || 0

export const getFirstVendorID = rows => {
  const rowWithVendorData = rows.find(({ vendorOverride, extraData }) => vendorOverride || (extraData && extraData.VendorIdd))
  if (rowWithVendorData) {
    return rowWithVendorData.vendorOverride || rowWithVendorData.extraData.VendorIdd
  }
  return 0
}

export function useDebouncedValue(input, time = 500) {
  const [debouncedValue, setDebouncedValue] = useState(input)

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedValue(input), time)
    return () => clearTimeout(timeout)
  }, [input, time])

  return debouncedValue
}

export const createRenderComponent = () => {
  let storedRender = () => null
  const components = new Set()
  class RenderComponent extends React.Component {
    UNSAFE_componentWillMount() {
      components.add(this)
    }
    componentWillUnmount() {
      components.delete(this)
    }
    render() {
      return storedRender(this.props)
    }
  }
  return (render) => {
    storedRender = render
    Array.from(components.values())
      .forEach(component => component.forceUpdate())
    return RenderComponent
  }
}

export const useDidUpdateEffect = (create, deps) => {
  const didMountRef = useRef(false)

  useEffect(() => {
    if (didMountRef.current)
      create()
    else
      didMountRef.current = true
  }, deps)
}

export const selectorFields = ['user', 'crew', 'vendor', 'event', 'boolean']

export function formatDateRange(DateTimeStart, DateTimeEnd) {
  const formatString = 'MMM D YYYY'
  return `${DateTimeStart ? moment(DateTimeStart).format(formatString) : 'No Start Date'} - ${DateTimeEnd ? moment(DateTimeEnd).format(formatString) : 'No End Date'}`
}

export const getDateRange = (Rows) => {
  const formatString = 'MMM D YYYY'
  const { DateTimeStart, DateTimeEnd } = Rows.reduce((accum, { DateTimeStart, DateTimeEnd }) => {
    if (DateTimeStart) {
      const momentStart = moment(DateTimeStart)
      if (!accum.DateTimeStart || momentStart.isBefore(accum.DateTimeStart)) {
        accum.DateTimeStart = momentStart
      }
    }
    if (DateTimeEnd) {
      const momentEnd = moment(DateTimeEnd)
      if (!accum.DateTimeEnd || momentEnd.isAfter(accum.DateTimeEnd)) {
        accum.DateTimeEnd = momentEnd
      }
    }
    return accum
  }, {})
  return `${DateTimeStart ? DateTimeStart.format(formatString) : 'No Start Date'} - ${DateTimeEnd ? DateTimeEnd.format(formatString) : 'No End Date'}`
}
